import React from "react"
import autobind from "class-autobind"
import { Dialog, FlatButton, TextField, Toggle, SelectField, MenuItem, RadioButton, RadioButtonGroup, Checkbox } from "material-ui"
import strings from "../../localization/strings"
import SimpleTimeEditor from "../common/SimpleTimeEditor"
import { IBreak, ISimpleTime, EBreakType } from "../../models"
import * as utils from "../../helpers/utils"
import AccountStore from "../../stores/AccountStore"
import * as scheduleHelper from "../../helpers/scheduleHelper"
import { red500 } from "material-ui/styles/colors"

interface IProps {
  editBreak: IBreak
  breaks: IBreak[]
  handleClose(): void
  handleAdd(payload: IBreak): void
  handleUpdate(payload: IBreak): void

  // mobile break props
  isMobileBreak?: boolean

  // shift break props
  restrictedStartTime?: ISimpleTime
  restrictedEndTime?: ISimpleTime
}
interface IState {
  type: EBreakType
  name: string
  errorName: string
  breakWindowStartTime: ISimpleTime
  breakWindowEndTime: ISimpleTime
  errorBreakWindowTime: string
  durationToTrigger: number
  errorDurationToTrigger: string
  minimumBreakDuration: number
  errorMinimumBreakDuration: string
  maximumBreakDuration: number
  errorMaximumBreakDuration: string
  recommendedBreakDuration: number
  errorRecommendedBreakDuration: string
  requirePunchBiometrics: boolean
  payTypeId: string
  errorPayTypeId: string
  disableLocException: boolean
  disableBioException: boolean
  allowMinBreakOverride: boolean
  enableOverrideMinBreakDuration: boolean
  overrideMinBreakDuration: number
  errorOverrideMinBreakDuration: string
  alternatePayTypeId: string
  errorAlternatePayTypeId: string
}

export default class AddBreakDialog extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props)
    this.state = {
      type: props.editBreak ? props.editBreak.Type : EBreakType.TimeWindow,
      name: props.editBreak ? props.editBreak.Name : "",
      errorName: "",
      breakWindowStartTime: props.editBreak ? props.editBreak.BreakWindowStartTime : AccountStore.ScheduleVirtualSimpleTime,
      breakWindowEndTime: props.editBreak ? props.editBreak.BreakWindowEndTime : AccountStore.ScheduleVirtualSimpleTime,
      errorBreakWindowTime: "",
      durationToTrigger: props.editBreak ? props.editBreak.DurationToTrigger : 0,
      errorDurationToTrigger: "",
      minimumBreakDuration: props.editBreak ? props.editBreak.MinimumBreakDuration : 15,
      errorMinimumBreakDuration: "",
      maximumBreakDuration: props.editBreak ? props.editBreak.MaximumBreakDuration : 60,
      errorMaximumBreakDuration: "",
      recommendedBreakDuration: props.editBreak ? props.editBreak.RecommendedBreakDuration : 60,
      errorRecommendedBreakDuration: "",
      requirePunchBiometrics: props.editBreak ? props.editBreak.RequirePunchBiometrics : false,
      payTypeId: props.editBreak ? props.editBreak.PayTypeId : null,
      errorPayTypeId: "",
      disableLocException: props.editBreak ? props.editBreak.DisLocEx : false,
      disableBioException: props.editBreak ? props.editBreak.DisBioEx : false,
      allowMinBreakOverride: props.editBreak ? props.editBreak.AllowMinBreakOverride : false,
      enableOverrideMinBreakDuration: props.editBreak && props.editBreak.OverrideMinBreakDuration > 0 ? true : false,
      overrideMinBreakDuration: props.editBreak ? props.editBreak.OverrideMinBreakDuration : null,
      errorOverrideMinBreakDuration: "",
      alternatePayTypeId: props.editBreak ? props.editBreak.AlternatePayTypeId : null,
      errorAlternatePayTypeId: "",
    }
    autobind(this)
  }

  handleSave() {
    if (!this.checkValidate()) return
    const payload: IBreak = {
      Id: this.props.editBreak ? this.props.editBreak.Id : utils.guid(),
      Name: this.state.name.trim(),
      Type: this.state.type,
      BreakWindowStartTime: this.state.type === EBreakType.TimeWindow ? this.state.breakWindowStartTime : null,
      BreakWindowEndTime: this.state.type === EBreakType.TimeWindow ? this.state.breakWindowEndTime : null,
      MinimumBreakDuration: this.state.minimumBreakDuration,
      MaximumBreakDuration: this.state.maximumBreakDuration,
      DurationToTrigger: this.state.type === EBreakType.TimeDuration ? this.state.durationToTrigger : 0,
      RecommendedBreakDuration: this.state.recommendedBreakDuration,
      RequirePunchBiometrics: this.state.requirePunchBiometrics,
      PayTypeId: this.state.payTypeId,
      DisLocEx: this.state.disableLocException,
      DisBioEx: this.state.disableBioException,
      AllowMinBreakOverride: this.state.allowMinBreakOverride,
      OverrideMinBreakDuration: this.state.allowMinBreakOverride && this.state.enableOverrideMinBreakDuration &&
        this.state.overrideMinBreakDuration > 0 ? this.state.overrideMinBreakDuration : null,
      AlternatePayTypeId: this.state.allowMinBreakOverride && this.state.alternatePayTypeId ? this.state.alternatePayTypeId : null,
    }
    this.props.editBreak ? this.props.handleUpdate(payload) : this.props.handleAdd(payload)
  }

  checkValidate() {
    let isValidate = true
    const durationTimeWindow = this.state.type === EBreakType.TimeWindow &&
      scheduleHelper.getDurationMiutesFromShiftSimpleTimes(this.state.breakWindowStartTime, this.state.breakWindowEndTime)

    if (!this.state.name.trim()) {
      this.setState({ errorName: strings.NameRequired })
      isValidate = false
    }
    else if (this.props.breaks.filter(i => this.props.editBreak ? i.Id !== this.props.editBreak.Id : true).find(item => item.Name === this.state.name.trim())) {
      this.setState({ errorName: strings.Break_name_is_duplicated })
      isValidate = false
    }
    else {
      this.setState({ errorName: "" })
    }

    if (this.state.type === EBreakType.TimeWindow) {
      if (this.props.restrictedStartTime && this.props.restrictedEndTime && !scheduleHelper.breakWindowTimesIsFallInShiftTimes(this.props.restrictedStartTime, this.props.restrictedEndTime, this.state.breakWindowStartTime, this.state.breakWindowEndTime)) {
        this.setState({ errorBreakWindowTime: strings.Break_Time_Window_should_be_fall_in_shift_time })
        isValidate = false
      }
      else if (durationTimeWindow < 5) {
        this.setState({ errorBreakWindowTime: strings.Duration_Time_Window_cannot_less_than_5min })
        isValidate = false
      }
      else {
        this.setState({ errorBreakWindowTime: "" })
      }
    }
    else {
      if (this.state.durationToTrigger <= 0 || this.state.durationToTrigger >= 1440) {
        this.setState({ errorDurationToTrigger: strings.formatString(strings.This_field_requires_a_number_between_min_and_max, 0, 1440) as string })
        isValidate = false
      }
      else {
        this.setState({ errorDurationToTrigger: "" })
      }
    }

    if (!this.state.minimumBreakDuration) {
      this.setState({ errorMinimumBreakDuration: strings.This_field_is_required })
      isValidate = false
    }
    else if (this.state.minimumBreakDuration < 5) {
      this.setState({ errorMinimumBreakDuration: strings.formatString(strings.This_field_must_be_greater_than_or_equal_to_x, 5) as string })
      isValidate = false
    }
    else {
      this.setState({ errorMinimumBreakDuration: "" })
    }

    if (!this.state.maximumBreakDuration) {
      this.setState({ errorMaximumBreakDuration: strings.This_field_is_required })
      isValidate = false
    }
    else if (this.state.type === EBreakType.TimeWindow && this.state.maximumBreakDuration > durationTimeWindow) {
      this.setState({ errorMaximumBreakDuration: strings.This_filed_should_be_less_than_Break_Time_Window_Duration })
      isValidate = false
    }
    else {
      this.setState({ errorMaximumBreakDuration: "" })
    }

    if (!this.state.recommendedBreakDuration) {
      this.setState({ errorRecommendedBreakDuration: strings.This_field_is_required })
      isValidate = false
    }
    else {
      this.setState({ errorRecommendedBreakDuration: "" })
    }

    if (this.state.minimumBreakDuration && this.state.minimumBreakDuration >= 5 &&
      this.state.maximumBreakDuration && (this.state.type === EBreakType.TimeWindow && this.state.maximumBreakDuration <= durationTimeWindow || this.state.type === EBreakType.TimeDuration) &&
      this.state.recommendedBreakDuration
    ) {
      if (this.state.maximumBreakDuration < this.state.minimumBreakDuration) {
        this.setState({ errorMaximumBreakDuration: strings.Max_Break_Duration_should_be_greater_than_Min_Break_Duration })
        isValidate = false
      }
      else {
        this.setState({ errorMaximumBreakDuration: "" })
      }
      if (this.state.recommendedBreakDuration < this.state.minimumBreakDuration) {
        this.setState({ errorRecommendedBreakDuration: strings.Recommended_Break_Duration_should_be_greater_than_Min_Break_Duration })
        isValidate = false
      }
      else if (this.state.recommendedBreakDuration > this.state.maximumBreakDuration) {
        this.setState({ errorRecommendedBreakDuration: strings.Recommended_Break_Duration_should_be_less_than_Max_Break_Duration })
        isValidate = false
      }
      else {
        this.setState({ errorRecommendedBreakDuration: "" })
      }
    }

    if (this.props.isMobileBreak && !this.state.payTypeId) {
      this.setState({ errorPayTypeId: strings.This_field_is_required })
      isValidate = false
    }
    else {
      this.setState({ errorPayTypeId: "" })
    }

    if (this.state.allowMinBreakOverride) {
      // if (this.state.alternatePayTypeId) {
      //   if (this.state.payTypeId === this.state.alternatePayTypeId) {
      //     this.setState({ errorAlternatePayTypeId: strings.Alternate_PayType_should_be_not_same_with_paytype })
      //     isValidate = false
      //   }
      //   else {
      //     this.setState({ errorAlternatePayTypeId: "" })
      //   }
      // }
      // else {
      //   this.setState({ errorAlternatePayTypeId: strings.This_field_is_required })
      //   isValidate = false
      // }

      if (this.state.enableOverrideMinBreakDuration) {
        if (!this.state.overrideMinBreakDuration) {
          this.setState({ errorOverrideMinBreakDuration: strings.This_field_is_required })
          isValidate = false
        }
        else {
          if (this.state.overrideMinBreakDuration < 5) {
            this.setState({ errorOverrideMinBreakDuration: strings.formatString(strings.This_field_must_be_greater_than_or_equal_to_x, 5) as string })
            isValidate = false
          }
          else {
            if (this.state.minimumBreakDuration && this.state.overrideMinBreakDuration > this.state.minimumBreakDuration) {
              this.setState({ errorOverrideMinBreakDuration: strings.greater_than_Minimum_Break_Duration })
              isValidate = false
            }
            else {
              this.setState({ errorOverrideMinBreakDuration: "" })
            }
          }
        }
        if (!this.state.alternatePayTypeId) {
          this.setState({ errorAlternatePayTypeId: strings.This_field_is_required })
          isValidate = false
        }
        else {
          if (this.state.payTypeId === this.state.alternatePayTypeId) {
            this.setState({ errorAlternatePayTypeId: strings.Alternate_PayType_should_be_not_same_with_paytype })
            isValidate = false
          }
          else {
            this.setState({ errorAlternatePayTypeId: "" })
          }
        }
        
      }
      else {
        this.setState({ errorOverrideMinBreakDuration: "" })
      }
    }
    else {
      this.setState({ errorAlternatePayTypeId: "", errorOverrideMinBreakDuration: "" })
    }
    return isValidate
  }

  handleChange(e, value) {
    if (utils.validateInteger(value)) {
      switch (e.target.name) {
        case "minimumBreakDuration":
          this.setState({ minimumBreakDuration: Number(value) }, () => this.checkValidate())
          break
        case "maximumBreakDuration":
          this.setState({ maximumBreakDuration: Number(value) }, () => this.checkValidate())
          break
        case "recommendedBreakDuration":
          this.setState({ recommendedBreakDuration: Number(value) }, () => this.checkValidate())
          break
        case "durationToTriggerBreak":
          this.setState({ durationToTrigger: Number(value) }, () => this.checkValidate())
          break
        case "overrideMinBreakDuration":
          this.setState({ overrideMinBreakDuration: Number(value) }, () => this.checkValidate())
          break
      }
    }
  }

  render() {
    return (
      <Dialog
        open
        autoScrollBodyContent
        title={this.props.editBreak ? strings.Edit_Break : strings.New_Break}
        titleStyle={{ paddingLeft: 35, paddingBottom: 10 }}
        actions={[
          <FlatButton
            data-intercom-target={"Break-Btn-CloseAddBreakDialog"}
            label={strings.CANCEL}
            secondary
            onClick={this.props.handleClose}
          />,
          <FlatButton
            data-intercom-target={"Break-Btn-SaveBreak"}
            label={strings.SAVE}
            primary
            onClick={this.handleSave}
          />
        ]}
      >
        <hr />
        <div style={{ paddingRight: 20, paddingLeft: 20 }}>
          <div style={{ width: "100%" }}>
            <TextField
              data-intercom-target={"Break-Entry-Name"}
              floatingLabelText={strings.Name}
              value={this.state.name}
              onChange={(e, value) => { this.setState({ name: value }, () => this.checkValidate()) }}
              errorText={this.state.errorName}
              errorStyle={{ position: "absolute", bottom: -10 }}
              fullWidth
              disabled={this.props.editBreak && this.props.editBreak.Used}
            />
          </div>
          <div style={{ display: "flex", marginTop: 20 }}>
            {this.props.isMobileBreak &&
              <RadioButtonGroup
                name="BreakType"
                valueSelected={this.state.type}
                style={{ width: 180, marginRight: 25 }}
                onChange={(e, type) =>
                  this.setState({
                    type,
                    breakWindowStartTime: type === EBreakType.TimeWindow ? AccountStore.ScheduleVirtualSimpleTime : null,
                    breakWindowEndTime: type === EBreakType.TimeWindow ? AccountStore.ScheduleVirtualSimpleTime : null,
                    durationToTrigger: 0
                  }, () => this.checkValidate())
                }
              >
                <RadioButton
                  data-intercom-target={"Break-RadioBtn-TimeWindow"}
                  value={EBreakType.TimeWindow}
                  label={strings.Time_Window}
                  style={{ marginBottom: 10 }}
                />
                <RadioButton
                  data-intercom-target={"Break-RadioBtn-TimeDuration"}
                  value={EBreakType.TimeDuration}
                  label={strings.Time_Duration}
                />
              </RadioButtonGroup>
            }
            <div>
              {this.state.type === EBreakType.TimeWindow ?
                <div style={{ display: "flex", position: "relative", marginTop: -16 }}>
                  <div style={{ color: "rgb(244, 67, 54)", position: "absolute", fontSize: 12, bottom: -15 }}>{this.state.errorBreakWindowTime}</div>
                  <div style={{ width: 140 }}>
                    <SimpleTimeEditor
                      label={strings.Break_Start_Time_Window}
                      simpleTime={this.state.breakWindowStartTime}
                      handleChange={breakWindowStartTime => {
                        const breakTime = scheduleHelper.getVirtualBreakTime(this.props.restrictedStartTime, this.props.restrictedEndTime, breakWindowStartTime, this.state.breakWindowEndTime)
                        this.setState({ breakWindowStartTime: breakTime.VitualBreakStartTime, breakWindowEndTime: breakTime.VirtualBreakEndTime }, () => this.checkValidate())
                      }}
                      readonly={this.props.editBreak && this.props.editBreak.Used}
                    />
                  </div>
                  <div style={{ marginLeft: 50, width: 140 }}>
                    <SimpleTimeEditor
                      label={strings.Break_End_Time_Window}
                      simpleTime={this.state.breakWindowEndTime}
                      handleChange={breakWindowEndTime => {
                        const breakTime = scheduleHelper.getVirtualBreakTime(this.props.restrictedStartTime, this.props.restrictedEndTime, this.state.breakWindowStartTime, breakWindowEndTime)
                        this.setState({ breakWindowStartTime: breakTime.VitualBreakStartTime, breakWindowEndTime: breakTime.VirtualBreakEndTime }, () => this.checkValidate())
                      }}
                      readonly={this.props.editBreak && this.props.editBreak.Used}
                    />
                  </div>
                </div> :
                <div style={{ position: "relative", marginTop: -16 }}>
                  <TextField
                    data-intercom-target={"Break-Entry-DurationToTriggerBreak"}
                    name="durationToTriggerBreak"
                    style={{ width: 180 }}
                    floatingLabelText={strings.Duration_to_Trigger_Break}
                    value={this.state.durationToTrigger}
                    errorText={this.state.errorDurationToTrigger}
                    errorStyle={{ position: "absolute", top: 70 }}
                    onChange={this.handleChange}
                  />
                  <span style={{ position: "absolute", top: 38, right: 10 }}>min</span>
                </div>
              }
            </div>
          </div>
          <div style={{ display: "flex", marginTop: 10 }}>
            <div style={{ position: "relative" }}>
              <TextField
                data-intercom-target={"Break-Entry-MinBreakDuration"}
                name="minimumBreakDuration"
                style={{ width: 180 }}
                floatingLabelText={strings.Minimum_Break_Duration}
                value={this.state.minimumBreakDuration}
                errorText={this.state.errorMinimumBreakDuration}
                errorStyle={{ position: "absolute", top: 70 }}
                onChange={this.handleChange}
                disabled={this.props.editBreak && this.props.editBreak.Used}
              />
              <span style={{ position: "absolute", top: 38, right: 10 }}>min</span>
            </div>
            <div style={{ position: "relative", marginLeft: 25 }}>
              <TextField
                data-intercom-target={"Break-Entry-MaxBreakDuration"}
                name="maximumBreakDuration"
                style={{ width: 180 }}
                floatingLabelText={strings.Maximum_Break_Duration}
                value={this.state.maximumBreakDuration}
                errorText={this.state.errorMaximumBreakDuration}
                errorStyle={{ position: "absolute", top: 70 }}
                onChange={this.handleChange}
                disabled={this.props.editBreak && this.props.editBreak.Used}
              />
              <span style={{ position: "absolute", top: 38, right: 10 }}>min</span>
            </div>
            <div style={{ position: "relative", marginLeft: 25 }}>
              <TextField
                data-intercom-target={"Break-Entry-RecommededBreakDuration"}
                name="recommendedBreakDuration"
                style={{ width: 220 }}
                floatingLabelText={strings.Recommended_Break_Duration}
                value={this.state.recommendedBreakDuration}
                errorText={this.state.errorRecommendedBreakDuration}
                errorStyle={{ position: "absolute", top: 70 }}
                onChange={this.handleChange}
                disabled={this.props.editBreak && this.props.editBreak.Used}
              />
              <span style={{ position: "absolute", top: 38, right: 10 }}>min</span>
            </div>
          </div>
          <div style={{ marginTop: 20, display: "flex" }}>
            <div style={{ flex: 1 }}>
              <SelectField
                data-intercom-target={"Break-Select-PayType"}
                floatingLabelText={strings.Pay_Type}
                style={{ width: 250 }}
                value={this.state.payTypeId}
                onChange={(e, key, payTypeId) => this.setState({ payTypeId }, () => this.checkValidate())}
                disabled={this.props.editBreak && this.props.editBreak.Used}
                errorText={this.state.errorPayTypeId}
              >
                {AccountStore.getCurrentAccount().PayTypes.map((item, index) =>
                  <MenuItem value={item.Id} primaryText={item.Name} key={index} disabled={item.Deleted} />
                )}
              </SelectField>
            </div>
            <div style={{ flex: 1 }}>
              <Toggle
                data-intercom-target={"Break-Toggle-RequirePunchBiometrics"}
                label={strings.Require_Punch_Biometrics}
                style={{ width: 275, marginTop: 35 }}
                toggled={this.state.requirePunchBiometrics}
                onToggle={(e, value) => this.setState({ requirePunchBiometrics: value })}
                disabled={this.props.editBreak && this.props.editBreak.Used}
              />
            </div>
          </div>
          <div style={{ marginTop: 20 }}>
            <Checkbox
              data-intercom-target={"Break-CheckBox-AllowMinimumBreakOverride"}
              label={strings.Allow_Minimum_Break_Override}
              checked={this.state.allowMinBreakOverride}
              disabled={this.props.editBreak && this.props.editBreak.Used}
              onCheck={(e, value) => this.setState({ allowMinBreakOverride: value, overrideMinBreakDuration: null, alternatePayTypeId: null }, () => this.checkValidate())}
            />
            {this.state.allowMinBreakOverride &&
              <div>
                <div style={{ marginTop: 20, display: "flex", alignItems: "center" }}>
                  <div>
                    <Toggle
                      data-intercom-target={"Break-Toggle-AlternatePayType"}
                      labelPosition="right"
                      label={strings.Alternate_PayType_if_break_is_less_than}
                      toggled={this.state.enableOverrideMinBreakDuration}
                      onToggle={(e, value) => this.setState({ enableOverrideMinBreakDuration: value, overrideMinBreakDuration: null }, () => this.checkValidate())}
                      disabled={this.props.editBreak && this.props.editBreak.Used}
                    />
                  </div>

                  <TextField
                    data-intercom-target={"Break-Entry-OverrideMinBreakDuration"}
                    name="overrideMinBreakDuration"
                    style={{ width: 40, marginLeft: 10, marginRight: 10 }}
                    value={this.state.overrideMinBreakDuration}
                    onChange={this.handleChange}
                    disabled={(this.props.editBreak && this.props.editBreak.Used) || !this.state.enableOverrideMinBreakDuration}
                  />
                  <span>mins</span>
                  {this.state.errorOverrideMinBreakDuration &&
                    <span style={{ color: red500, fontSize: 12, marginLeft: 10, marginTop: 10 }}>
                      {this.state.errorOverrideMinBreakDuration}
                    </span>
                  }
                </div>
                { this.state.enableOverrideMinBreakDuration && <div>
                  <SelectField
                    data-intercom-target={"Break-Select-AlternatePayType"}
                    floatingLabelText={strings.Alternate_PayType}
                    style={{ width: 250, marginTop: -10 }}
                    value={this.state.alternatePayTypeId}
                    onChange={(e, key, alternatePayTypeId) => this.setState({ alternatePayTypeId }, () => this.checkValidate())}
                    disabled={this.props.editBreak && this.props.editBreak.Used}
                    errorText={this.state.errorAlternatePayTypeId}
                  >
                    {AccountStore.getCurrentAccount().PayTypes.map((item, index) =>
                      <MenuItem value={item.Id} primaryText={item.Name} key={index} disabled={item.Deleted} />
                    )}
                  </SelectField>
                </div>}
              </div>
            }
          </div>
          {/* implemented "Disable (XYZ) Exception" feature, but still discussing, so hide it.
          https://www.pivotaltracker.com/story/show/184730661/comments/237986279
          <div style={{ marginTop: 25, display: "flex" }}>
            <div style={{ flex: 1 }}>
              <Toggle
                data-intercom-target={"Break-Toggle-DisableLocationException"}
                style={{ width: 275 }}
                label={strings.Disable_Location_Exception}
                toggled={this.state.disableLocException}
                onToggle={(e, value) => this.setState({ disableLocException: value })}
                disabled={this.props.editBreak && this.props.editBreak.Used}
              />
            </div>
            <div style={{ flex: 1 }}>
              <Toggle
                data-intercom-target={"Break-Toggle-DisableBiometricException"}
                style={{ width: 275 }}
                label={strings.Disable_Biometrics_Exception}
                toggled={this.state.disableBioException}
                onToggle={(e, value) => this.setState({ disableBioException: value })}
                disabled={this.props.editBreak && this.props.editBreak.Used}
              />
            </div>
          </div> 
          */}
        </div>
      </Dialog>
    )
  }
}